import React, { useCallback, useContext, useEffect, useState } from "react";
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from "react-router-dom";
import NotFoundPage from "pages/Error/NotFoundPage";
import { useTranslation } from "react-i18next";
import GlobalContext from "contexts/GlobalContext";
import { generateMiddleWare, routesMap } from "./tools";
import { useTranslator } from "@sia/style-guide";
import { initializeSalaryFilters } from "./Salary/stateInitializers";
import { updateWagesPageState } from "./Salary/stateUpdaters";
import { initializeOverheadFilters } from "./Overheads/stateInitializers";
import { updateOverheadPageState } from "./Overheads/stateUpdaters";
import { initializeKeyfiguresFilters } from "./KeyFigures/stateInitializer";
import { updateKeyFiguresPageState } from "./KeyFigures/stateUpdaters";
import { initializeWorkingHoursFilters } from "./WorkingHours/stateInitializers";
import { updateWorkingHoursPageState } from "./WorkingHours/stateUpdaters";
import SalaryPage from "./Salary/SalaryPage";
import OverheadsPage from "./Overheads/OverheadsPage";
import WorkingHoursPage from "./WorkingHours/WorkingHoursPage";
import KeyFiguresPage from "./KeyFigures/KeyFiguresPage";
import useEvaluationMasterKeyAccess from "./useEvaluationMasterKeyAccess";
import { useEvaluationRequestScope } from "./evaluationRequestScope";

const reportUpdateFailure = (request, area, error) => {
  if (request.isCurrent() && error?.code !== "CRYPTO_SESSION_CHANGED") console.error(`Failed to load ${area} Evaluation data`, error);
};

export default function EvaluationsRoutes() {
  const { user } = useContext(GlobalContext);
  const translate = useTranslator();
  const { i18n } = useTranslation();
  const history = useHistory();
  const { languageKey } = useParams();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const access = useEvaluationMasterKeyAccess(user);
  const dataScopeKey = `${access.dataScopeKey}:${languageKey}`;

  const [salaryFilters, setSalaryFilters] = useState(initializeSalaryFilters(translate));
  const [salaryData, setSalaryData] = useState([]);
  const [overheadsFilters, setOverheadsFilters] = useState(initializeOverheadFilters(translate));
  const [overheadsData, setOverheadsData] = useState([]);
  const [workingHoursFilters, setWorkingHoursFilters] = useState(initializeWorkingHoursFilters(translate));
  const [workingHoursData, setWorkingHoursData] = useState({ data: [], labels: {} });
  const [keyFiguresFilters, setKeyFiguresFilters] = useState(initializeKeyfiguresFilters(translate));
  const [keyFiguresData, setKeyFiguresData] = useState([]);

  useEffect(() => {
    const route = Array.from(routesMap.values()).find(({ url }) => url === location.pathname.split("/")[3]);
    if (route) setSelectedTab(route.tabIndex);
  }, [location.pathname]);

  const resetEvaluationState = useCallback(() => {
    setSalaryData([]);
    setOverheadsData([]);
    setWorkingHoursData({ data: [], labels: {} });
    setKeyFiguresData([]);
    setSalaryFilters(initializeSalaryFilters(translate));
    setOverheadsFilters(initializeOverheadFilters(translate));
    setWorkingHoursFilters(initializeWorkingHoursFilters(translate));
    setKeyFiguresFilters(initializeKeyfiguresFilters(translate));
  }, [translate]);
  const { ready: isDataScopeReady, beginRequest } = useEvaluationRequestScope(dataScopeKey, resetEvaluationState);

  useEffect(() => {
    if (!isDataScopeReady || !salaryFilters.newest || !user || !access.canFetch || selectedTab !== 0) return;
    const request = beginRequest("salary");
    void updateWagesPageState(
      access.isCompanyMode,
      languageKey,
      salaryFilters,
      request.guard(setSalaryFilters),
      request.guard(setSalaryData)
    ).catch((error) => reportUpdateFailure(request, "salary", error));
  }, [salaryFilters, user, access.canFetch, access.isCompanyMode, beginRequest, isDataScopeReady, selectedTab, languageKey]);

  useEffect(() => {
    if (!isDataScopeReady || !overheadsFilters.newest || !user || !access.canFetch || selectedTab !== 1) return;
    const request = beginRequest("overheads");
    void updateOverheadPageState(
      access.isCompanyMode,
      languageKey,
      overheadsFilters,
      request.guard(setOverheadsFilters),
      request.guard(setOverheadsData),
      translate
    ).catch((error) => reportUpdateFailure(request, "overheads", error));
  }, [overheadsFilters, user, access.canFetch, access.isCompanyMode, beginRequest, isDataScopeReady, selectedTab, languageKey, translate]);

  useEffect(() => {
    if (!isDataScopeReady || !workingHoursFilters.newest || !user || !access.canFetch || selectedTab !== 2) return;
    const request = beginRequest("working-hours");
    void updateWorkingHoursPageState(
      access.isCompanyMode,
      languageKey,
      workingHoursFilters,
      request.guard(setWorkingHoursFilters),
      request.guard(setWorkingHoursData)
    ).catch((error) => reportUpdateFailure(request, "working-hours", error));
  }, [workingHoursFilters, user, access.canFetch, access.isCompanyMode, beginRequest, isDataScopeReady, selectedTab, languageKey]);

  useEffect(() => {
    if (!isDataScopeReady || !keyFiguresFilters.newest || !user || !access.canFetch || selectedTab !== 3) return;
    const request = beginRequest("key-figures");
    void updateKeyFiguresPageState(
      access.isCompanyMode,
      languageKey,
      keyFiguresFilters,
      request.guard(setKeyFiguresFilters),
      request.guard(setKeyFiguresData)
    ).catch((error) => reportUpdateFailure(request, "key-figures", error));
  }, [keyFiguresFilters, user, access.canFetch, access.isCompanyMode, beginRequest, isDataScopeReady, selectedTab, languageKey]);

  const middleware = generateMiddleWare(i18n, languageKey, user, translate, selectedTab, setSelectedTab, history, access);
  return (
    <Switch>
      <Route
        path="/:languageKey/stats/wages"
        exact
        render={() =>
          middleware(
            <SalaryPage
              filters={salaryFilters.filters}
              setFilters={setSalaryFilters}
              data={salaryData}
              translate={translate}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          )
        }
      />
      <Route
        path="/:languageKey/stats/overhead-costs"
        exact
        render={() =>
          middleware(
            <OverheadsPage
              filters={overheadsFilters.filters}
              setFilters={setOverheadsFilters}
              data={overheadsData}
              translate={translate}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          )
        }
      />
      <Route
        path="/:languageKey/stats/hours-worked"
        exact
        render={() =>
          middleware(
            <WorkingHoursPage
              filters={workingHoursFilters.filters}
              setFilters={setWorkingHoursFilters}
              data={workingHoursData}
              translate={translate}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          )
        }
      />
      <Route
        path="/:languageKey/stats/key-operating-figures"
        exact
        render={() =>
          middleware(
            <KeyFiguresPage
              filters={keyFiguresFilters.filters}
              setFilters={setKeyFiguresFilters}
              data={keyFiguresData}
              translate={translate}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          )
        }
      />
      <Route exact path={`/${languageKey}/stats`} render={() => middleware(<Redirect to={`/${languageKey}/stats/${routesMap.get("salary").url}`} />)} />
      <Route path={`/${languageKey}/stats/*`} render={() => <NotFoundPage />} />
    </Switch>
  );
}
